
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Calendar, LayoutDashboard, Menu, Users, Settings, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Navbar: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();

  // Log wanneer Navbar component wordt geladen
  useEffect(() => {
    console.log('Navbar component geladen, authenticatie status:', isAuthenticated ? 'ingelogd' : 'niet ingelogd');
  }, [isAuthenticated]);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Personeel', href: '/employees', icon: Users },
    { name: 'Rooster', href: '/schedule', icon: Calendar },
    { name: 'Instellingen', href: '/settings', icon: Settings },
  ];

  const closeSidebar = () => setSidebarOpen(false);
  
  const getInitials = (name: string) => {
    return name
      ? name
          .split(' ')
          .map(part => part[0])
          .join('')
          .toUpperCase()
      : 'U';
  };

  const handleNavigation = (href: string) => {
    console.log(`Navigeren naar: ${href}`);
    closeSidebar();
  };

  return (
    <>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={closeSidebar}
        />
      )}
      
      {/* Sidebar for mobile and desktop */}
      <div
        className={cn(
          "fixed top-0 left-0 h-full bg-sidebar border-r border-sidebar-border w-64 p-4 z-50 transform transition-transform duration-300 ease-in-out",
          sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
      >
        <div className="flex justify-between items-center mb-6">
          <Link to="/" className="flex items-center space-x-2" onClick={() => handleNavigation('/')}>
            <Calendar className="h-6 w-6 text-primary" />
            <span className="text-xl font-semibold">RoosterApp</span>
          </Link>
          <Button 
            variant="ghost" 
            size="icon" 
            className="md:hidden" 
            onClick={closeSidebar}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        <nav className="space-y-1 mt-8">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                "flex items-center px-3 py-2 text-sm rounded-md transition-all group",
                location.pathname === item.href
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-secondary"
              )}
              onClick={() => handleNavigation(item.href)}
            >
              <item.icon 
                className={cn(
                  "mr-3 h-5 w-5",
                  location.pathname === item.href
                    ? "text-primary-foreground"
                    : "text-muted-foreground group-hover:text-foreground"
                )}
              />
              {item.name}
            </Link>
          ))}
        </nav>
        
        {isAuthenticated && user && (
          <div className="absolute bottom-4 left-0 right-0 px-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full flex items-center justify-start px-3 py-2">
                  <Avatar className="h-8 w-8 mr-2">
                    <AvatarImage src="/placeholder.svg" alt={user?.name || 'Gebruiker'} />
                    <AvatarFallback>{getInitials(user?.name || '')}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col items-start">
                    <span className="text-sm font-medium">{user?.name || 'Gebruiker'}</span>
                    <span className="text-xs text-muted-foreground">{user?.role || 'Gebruiker'}</span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Mijn Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={() => console.log('Profiel bekijken')}>
                    Profiel
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleNavigation('/settings')}>
                    Instellingen
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => {
                  console.log('Uitloggen aangeroepen');
                  logout();
                }}>
                  Uitloggen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
      
      {/* Mobile header */}
      <div className="sticky top-0 z-30 flex h-16 items-center bg-background/95 backdrop-blur md:hidden border-b px-4">
        <Button 
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(true)}
          className="mr-2"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <Link to="/" className="flex items-center space-x-2" onClick={() => handleNavigation('/')}>
          <Calendar className="h-6 w-6 text-primary" />
          <span className="text-xl font-semibold">RoosterApp</span>
        </Link>
      </div>
    </>
  );
};

export default Navbar;
